import { Check, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * Выпадающий отбор НЕСКОЛЬКИХ значений галочками — вид и поведение как у `Combo`
 * (кнопка с подписью слева, попап под ней, клик мимо и Escape закрывают).
 *
 * Клик по строке переключает галочку и попап НЕ закрывает: несколько месяцев
 * отмечают подряд (разметка 20.10.26 на «Лиды → По дням»: «отмечаю сентябрь,
 * август, июнь»). Строка «все» снимает отбор. Значения — строки, как в адресе.
 */

export type PickOption = { value: string; label: string; short?: string };

export function MultiPick({
  label,
  values,
  options,
  onChange,
  all = "все",
  width = 220,
}: {
  label: string;
  values: string[];
  options: PickOption[];
  onChange: (values: string[]) => void;
  /** Подпись пустого отбора и строки, которая его снимает. */
  all?: string;
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const chosen = options.filter((o) => values.includes(o.value));
  // Порядок отбора — порядок списка, а не кликов: «июн, авг, сен» читается одинаково при любом пути.
  const toggle = (v: string) =>
    onChange(options.map((o) => o.value).filter((x) => (x === v ? !values.includes(x) : values.includes(x))));
  const row = "flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-[12.5px] hover:bg-bg-hover";

  return (
    <div ref={boxRef} className="relative" data-annot={`отбор ${label}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={chosen.length ? chosen.map((o) => o.label).join(", ") : all}
        className={
          "flex h-8 items-center overflow-hidden rounded-[var(--radius-ui)] border bg-bg text-[13px] " +
          (open ? "border-border-strong" : "border-border hover:border-border-strong")
        }
        style={{ maxWidth: width }}
      >
        <span className="flex h-full flex-none items-center whitespace-nowrap border-r border-border bg-bg-subtle px-2 text-[11.5px] text-fg-faint">
          {label}
        </span>
        <span className={`truncate px-2 ${chosen.length ? "text-fg" : "text-fg-muted"}`}>
          {chosen.length ? chosen.map((o) => o.short ?? o.label).join(", ") : all}
        </span>
        <ChevronDown size={13} className="mr-1.5 ml-auto flex-none text-fg-faint" />
      </button>

      {open ? (
        <div
          className="absolute top-[34px] left-0 z-30 overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-elevated shadow-lg"
          style={{ width: Math.max(width, 200) }}
          data-popup=""
        >
          <div className="max-h-[340px] overflow-auto py-1">
            <button type="button" onClick={() => onChange([])} className={`${row} border-b border-border text-fg-muted`}>
              <span className="truncate">{all}</span>
              {chosen.length === 0 ? <Check size={12} className="ml-auto flex-none text-accent-text" /> : null}
            </button>
            {options.map((o) => {
              const on = values.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={on}
                  onClick={() => toggle(o.value)}
                  className={`${row} ${on ? "text-accent-text" : "text-fg"}`}
                >
                  <span
                    className={`grid size-3.5 flex-none place-items-center rounded-[3px] border ${
                      on ? "border-accent bg-accent text-bg" : "border-border-strong"
                    }`}
                  >
                    {on ? <Check size={10} strokeWidth={3} /> : null}
                  </span>
                  <span className="truncate">{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
